<template>
  <div>
    <h1 class="page-header">{{ $t('payBasicSalary.pageTitle') }}</h1>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-sm-12 col-md-10">
            <div class="row">
              <div class="ts-flex">
                <ts-button color="info" class="mr-3 ml-2" outline @click="showForm()">
                  {{ $t('addNew') }}
                </ts-button>
                <ts-checkbox v-model="historyValue" @change="fetchAllIncludeHistory">{{
                  $t('payBasicSalary.includeHistory')
                }}</ts-checkbox>
              </div>
            </div>
          </div>
          <div class="col-sm-12 col-md-2 d-flex justify-content-end">
            <ts-input v-model="search" :placeholder="$t('search')" />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table :columns="columns" :records="resources" :loading="loading">
          <template v-slot="{ record, index }">
            <td class="ts-whitespace-no-wrap">{{ record.employee.card_id }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.employee.employee_name_kh }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.employee.employee_name_en }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.employee.logical_position.position_name_en }}</td>
            <td class="ts-whitespace-no-wrap text-center">{{ record.employee.payment_type.payment_type }}</td>
            <td class="text-right">{{ record.salary }}</td>
            <td class="text-right">{{ record.salary_after_probation }}</td>
            <td class="ts-whitespace-no-wrap text-center">
              <span class="badge badge-success" v-if="record.payroll_visible">Yes</span>
              <span class="badge badge-danger" v-else>No</span>
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.effective_date }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.created_date }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              <a href="#" v-tooltip="'Edit'" class="text-primary mr-2" @click.prevent="edit(record)">
                <i class="fas fa-edit"></i>
              </a>
              <a
                href="#"
                v-tooltip="'Delete'"
                class="text-danger"
                @click.prevent="destroy(record)"
                :disabled="record._deleting"
              >
                <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
                <i class="fas fa-trash" v-else></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FormComponent from './form.vue'

export default {
  name: 'pay-basic-salary',
  components: {
    FormComponent
  },
  data() {
    return {
      loading: false,
      historyValue: false
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('hr/payBasicSalary', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.hr.payBasicSalary.search
      },
      set(newValue) {
        this.$store.commit('hr/payBasicSalary/SET_SEARCH', newValue)
        this.$store.commit('hr/payBasicSalary/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          name: this.$t('payBasicSalary.cardId'),
          sortKey: 'employee.card_id'
        },
        {
          name: this.$t('payBasicSalary.nameKH'),
          sortKey: 'employee.employee_name_kh'
        },
        {
          name: this.$t('payBasicSalary.nameEN'),
          sortKey: 'employee.employee_name_en'
        },
        {
          name: this.$t('payBasicSalary.position')
        },
        {
          name: this.$t('payBasicSalary.paymentType'),
          sortKey: 'employee.sex',
          style: 'text-align:center'
        },
        {
          name: this.$t('payBasicSalary.salary'),
          sortKey: 'salary',
          style: 'text-align:right'
        },
        {
          name: this.$t('payBasicSalary.salaryAfterProbation'),
          sortKey: 'salary_after_probation',
          style: 'text-align:right'
        },
        {
          name: this.$t('payBasicSalary.payrollVisible'),
          style: 'text-align:center;'
        },
        {
          name: this.$t('payBasicSalary.effectiveDate'),
          style: 'text-align:center;'
        },
        {
          name: this.$t('payBasicSalary.created_date'),
          style: 'text-align:center;'
        },
        { name: this.$t('actions'), style: 'text-align:center; width: 7%;' }
      ]
    }
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('hr/payBasicSalary/fetch', attributes)
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    showForm(editData) {
      this.$modal.show(
        FormComponent,
        {
          editData: editData
        },
        {
          height: 'auto',
          width: '400px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    },
    edit(record) {
      this.showForm(record)
    },
    fetchAllIncludeHistory() {
      this.$store.commit('hr/payBasicSalary/SET_HISTORY_VALUE', this.historyValue | 0)
      this.fetchData()
    },
    async destroy(record) {
      if (await this.$confirmModal.danger(this.$t('are_you_sure_to_delete'))) {
        record._deleting = true
        this.$store
          .dispatch('hr/payBasicSalary/destroy', record.id)
          .then(response => {
            this.fetchData()
            this.$toasted.success(response.message)
            record._deleting = false
          })
          .catch(error => {
            this.$toasted.error(error.message)
            record._deleting = false
          })
      }
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('hr/payBasicSalary/RESET_STATE')
    next()
  }
}
</script>
<style lang="scss" scoped>
table td {
  vertical-align: middle;
}
::-webkit-scrollbar {
  height: 8px;
  width: 3px; /* Remove scrollbar space */
  background: #ffff; /* Optional: just make scrollbar invisible */
}

/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #e5e7e7;
}
div::-webkit-scrollbar-thumb:hover {
  background: #b7b7b8;
}
</style>
